<template>
  <v-toolbar app :clipped-left="$vuetify.breakpoint.mdAndUp" fixed color="primary" dark class="top-menubar">
    <v-toolbar-title class="ml-0">
      <v-toolbar-side-icon
        v-if="isLoggedIn && showMenuIcon && !jobIdToShowInHeader"
        @click.stop="$emit('setNavDrawer')"
      ></v-toolbar-side-icon>
      <JobWarnings v-if="showWarning && jobId" :job-id="jobId"></JobWarnings>
      <span v-if="showJobAddress" class="hidden-sm-and-down">
        {{ jobAddress }}
      </span>
      <span v-else-if="showDashBoardName !== ''" class="hidden-sm-and-down">
        <span v-if="!jobIdToShowInHeader">{{ showDashBoardName }}</span>
        <span v-else>Job Id: {{ jobIdToShowInHeader }}</span>
      </span>
      <span v-else class="hidden-sm-and-down">SIMPLIFi</span>
    </v-toolbar-title>
    <v-spacer></v-spacer>

    <UniversalSearch v-if="isLoggedIn && !jobIdToShowInHeader" :parent-component="'App'" />
    <Notification
      v-if="isLoggedIn"
      :notification-snackbar="notificationSnackbar"
      @update:notificationSnackbar="$emit('update:notificationSnackbar', $event)"
    ></Notification>
    <div v-if="addSpace" class="menu-spacer"></div>

    <TwilioClient
      v-show="!showLiveLocation"
      v-if="isLoggedIn && isUserLoggedIn"
      @showTwilioSnackbar="(msg, counter) => $emit('showTwilioSnackbar', msg, counter)"
      @clearTwilioSnackbar="$emit('clearTwilioSnackbar')"
      @handleStatusUpdate="(isOffline) => $emit('handleStatusUpdate', isOffline)"
    ></TwilioClient>

    <AppSwitcher v-if="isLoggedIn" />
    <UserMenu
      v-if="isLoggedIn"
      :image-snackbar="imageSnackbar"
      :image-snackbar-text="imageSnackbarText"
      @update:imageSnackbar="$emit('update:imageSnackbar', $event)"
      @update:imageSnackbarText="$emit('update:imageSnackbarText', $event)"
    />
  </v-toolbar>
</template>

<script lang="ts">
import { Component, Vue, Prop } from 'vue-property-decorator'
import UserMenu from '@/components/navigation/UserMenu.vue'
import AppSwitcher from '@/components/navigation/AppSwitcher.vue'
import TwilioClient from '@/components/twilio/TwilioClient.vue'
import eventBus from '@/common/bus'
import Job from '@/models/Job'
import storeGetters from '@/storeGetters'
import JobWarnings from '@/components/JobWarnings.vue'
import Store from '@/store'
import VulnerabilityQAModel from '@/models/claim/VulnerabilityQAModel'
import HealthAndSafetyQAModel from '@/models/claim/HealthAndSafetyQAModel'
import ComplaintModel from '@/models/claim/ComplaintModel'
import UniversalSearch from '@/components/timeline/UniversalSearch.vue'
import CustomerToPayModel from '@/models/claim/CustomerToPayModel'
import Notification from '@/components/notification/Notification.vue'
import JobEscalationDetail from '@/models/claim/JobEscalationDetailModel'

@Component({
  components: {
    UserMenu,
    AppSwitcher,
    TwilioClient,
    JobWarnings,
    UniversalSearch,
    Notification,
  },
})
export default class AppToolbar extends Vue {
  @Prop({ type: Boolean }) readonly notificationSnackbar!: boolean
  @Prop({ type: Boolean }) readonly imageSnackbar!: boolean
  @Prop({ type: String }) readonly imageSnackbarText!: string

  private jobIdToShowInHeader = ''
  private showLiveLocation = false
  private jobAddress = ''

  private onShowJobIdInHeader = (jobId = '') => {
    this.jobIdToShowInHeader = jobId
  }

  private onShowEngineerLiveLocation = (showLiveLocation: boolean) => {
    this.showLiveLocation = showLiveLocation
  }

  public mounted(): void {
    eventBus.$on('showJobIdInHeader', this.onShowJobIdInHeader)
    eventBus.$on('showEngineerLiveLocation', this.onShowEngineerLiveLocation)
  }

  public beforeDestroy(): void {
    eventBus.$off('showJobIdInHeader', this.onShowJobIdInHeader)
    eventBus.$off('showEngineerLiveLocation', this.onShowEngineerLiveLocation)
  }

  public get isLoggedIn(): boolean {
    return Store.Instance.state.SessionDetail.accessToken !== ''
  }

  private get isUserLoggedIn(): boolean {
    return Store.Instance.state.SessionDetail.detailRecordType === 'UserDetail'
  }

  private get showMenuIcon(): boolean {
    if (
      this.$route &&
      (this.$route.name === 'home' || this.$route.name === 'contractorManagement') &&
      Store.Instance.state.SessionDetail.detailRecordType === 'EngineerDetail'
    ) {
      return false
    }
    return true
  }

  private get job(): Job | null {
    return storeGetters.getJob(this.$route.params.jobId)
  }

  private get jobId(): string | undefined {
    if ((this.$route && this.$route.name === 'job') || this.$route.name === 'jobWithEngineerRequestId') {
      return this.$route.params.jobId
    }
    return undefined
  }

  private get showJobAddress(): boolean {
    if ((this.$route && this.$route.name === 'job') || this.$route.name === 'jobWithEngineerRequestId') {
      if (this.job) {
        this.jobAddress = 'Job Id: ' + this.$route.params.jobId + ' - ' + this.job.getAddress(', ')
      }
      return true
    }
    return false
  }

  private get showDashBoardName(): string {
    if (this.$route) {
      switch (this.$route.name) {
        case 'incidentmanagerdashboard':
          return 'Incident Manager'
        case 'jobDashboard':
          return 'Job History'
        case 'dashboard':
          return 'Supervisor'
        case 'unlinkedPhoneCallsDashboard':
          return 'Unlinked PhoneCalls'
        case 'complaintTracker':
          return 'Complaints'
        case 'managerSupervisorDashboard':
          return 'Manager Supervisor'
        case 'undeployedEmergencyDashboard':
          return 'Undeployed Emergencies'
        case 'networkManagerDashboard':
          return 'Network Manager'
        case 'thirdPartyDashboard':
          return 'Third Party'
        case 'queueDashboard':
          return 'Queue'
        case 'outstandingJobDashboard':
          return 'Work in Progress'
        case 'engineerRequestDashboard':
          return 'Engineer Request'
        case 'dynamicDashboard':
          return 'Custom Dashboard'
        case 'ctpDashboard':
          return 'Customer To Pay'
        case 'sIJobDashboard':
          return 'SI Dashboard'
        case 'missedJobsDashboard':
          return 'Open Jobs'
        case 'usJobDashboard':
          return 'US Dashboard'
        case 'clientInvoiceDashboardSI':
          return 'Client Invoice SI'
        case 'clientInvoiceDashboard':
          return 'Client Invoice'
        default:
          return ''
      }
    }
    return ''
  }

  private get vulnerabilityQAItem(): VulnerabilityQAModel | undefined {
    if (this.jobId) {
      return storeGetters.getVulnerabilityQA(this.jobId)
    }
    return undefined
  }

  private get healthAndSafetyQAItem(): HealthAndSafetyQAModel[] {
    if (this.jobId) {
      return storeGetters.getHealthAndSafetyQA(this.jobId)
    }
    return []
  }

  private get complaints(): ComplaintModel[] {
    return this.jobId ? storeGetters.getComplaints(this.jobId) : []
  }

  private get getIsVipCustomer(): boolean {
    if (this.jobId) {
      return this.job && this.job.isVIPCustomer ? true : false
    }
    return false
  }

  private get customerToPay(): CustomerToPayModel | undefined {
    if (this.jobId) {
      return storeGetters.getAuthorisedCustomerToPay(this.jobId)
    }
    return undefined
  }

  private get escalationDetails(): JobEscalationDetail | null {
    return this.jobId ? storeGetters.getJobActiveEscalationDetails(this.jobId) : null
  }

  private get showWarning(): boolean {
    let warning = false
    if (this.vulnerabilityQAItem && this.vulnerabilityQAItem.isHealthAffected === true) {
      warning = true
    }
    if (!warning && this.healthAndSafetyQAItem) {
      this.healthAndSafetyQAItem.forEach((element: HealthAndSafetyQAModel) => {
        if (element && element.isHealthAffected === true) {
          warning = true
          return
        }
      })
    }
    if (this.complaints && this.complaints.length > 0) {
      warning = this.complaints.filter((c) => !c.isResolved).length > 0 ? true : false
    }
    if (this.getIsVipCustomer) {
      warning = true
    }

    if (this.job && this.job.emergenciesQAs) {
      this.job.emergenciesQAs.forEach((emergency) => {
        if (emergency.asbestosEffectLevel) {
          warning = true
        }
      })
    }
    if (this.customerToPay && this.customerToPay.isTransactionCompleted) {
      warning = true
    }

    if (this.escalationDetails) {
      warning = true
    }

    if (this.job && this.job.jobType === 'US') {
      const filteredCustomerToPay =
        this.job && this.job.customerToPay && this.job.customerToPay.length > 0
          ? this.job.customerToPay.filter((x) => x.isTransactionCompleted === true)
          : []
      const totalPaidAmount =
        filteredCustomerToPay.length > 0
          ? filteredCustomerToPay.map((y) => y.amount).reduce((prev, next) => prev + next)
          : 0
      const emergency = this.job!.emergencies[this.job!.emergencies.length - 1]
      if (totalPaidAmount < (emergency && emergency.excessValue ? emergency.excessValue : 0)) {
        warning = true
      }
    }

    return warning
  }

  private get addSpace(): boolean {
    return Store.Instance.state.SessionDetail.detailRecordType === 'UserDetail'
  }
}
</script>

<style scoped>
.v-toolbar .menu-spacer {
  width: 60px;
  height: 56px;
  flex-shrink: 0;
}
.top-menubar {
  z-index: 11 !important;
}
</style>
